import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProfile } from '../../../store/slices/authSlice';
import api from '../../../store/axios';

function Section({ title, desc, children }) {
  return (
    <div className="dash-card" style={{ marginBottom: 20 }}>
      <div className="dash-card-head">
        <div>
          <div className="dash-card-title">{title}</div>
          {desc && <div className="dash-card-desc">{desc}</div>}
        </div>
      </div>
      <div className="dash-card-body">{children}</div>
    </div>
  );
}

function SaveFeedback({ state }) {
  if (state === 'saving') return <span style={{ fontSize: 12, color: 'var(--fg-dim)', fontFamily: 'var(--font-mono)' }}>Saving…</span>;
  if (state === 'saved') return <span style={{ fontSize: 12, color: 'var(--signal)', fontFamily: 'var(--font-mono)' }}>✓ Saved</span>;
  if (state === 'error') return <span style={{ fontSize: 12, color: '#ff6b6b', fontFamily: 'var(--font-mono)' }}>Failed — try again</span>;
  return null;
}

export default function Settings() {
  const dispatch = useDispatch();
  const { user, userType } = useSelector(s => s.auth);

  const firstName = user?.first_name || user?.firstName || '';
  const lastName = user?.last_name || user?.lastName || '';
  const email = user?.email || '';
  const userId = user?.id || user?._id || '';

  const [profile, setProfile] = useState({ firstName, lastName, email });
  const [pass, setPass] = useState({ current: '', next: '', confirm: '' });
  const [profileState, setProfileState] = useState('');
  const [passState, setPassState] = useState('');
  const [passError, setPassError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileState('saving');
    try {
      await api.patch('/auth/profile', {
        first_name: profile.firstName,
        last_name: profile.lastName,
        email: profile.email,
      });
      dispatch(fetchProfile());
      setProfileState('saved');
      setTimeout(() => setProfileState(''), 2500);
    } catch {
      setProfileState('error');
      setTimeout(() => setProfileState(''), 3000);
    }
  };

  const handlePassSave = async (e) => {
    e.preventDefault();
    if (pass.next !== pass.confirm) return;
    if (!userId) { setPassError('User ID not found — please sign out and sign in again.'); return; }
    setPassState('saving');
    setPassError('');
    try {
      await api.patch(`/auth/password/${userId}`, {
        currentPassword: pass.current,
        newPassword: pass.next,
      });
      setPass({ current: '', next: '', confirm: '' });
      setPassState('saved');
      setTimeout(() => setPassState(''), 2500);
    } catch (err) {
      setPassError(err.response?.data?.message || 'Password update failed');
      setPassState('error');
      setTimeout(() => setPassState(''), 3000);
    }
  };

  return (
    <div className="dash-page">
      <div className="dash-page-header">
        <div>
          <p className="dash-sub">ACCOUNT</p>
          <h1 className="dash-page-title">Settings</h1>
          <p className="dash-page-desc">Manage your profile, security, and preferences.</p>
        </div>
      </div>

      {/* Profile */}
      <Section title="Profile" desc="Your name and contact email.">
        <form onSubmit={handleProfileSave} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div className="field-row">
            <div className="field">
              <label>First name</label>
              <input type="text" value={profile.firstName} onChange={e => setProfile({ ...profile, firstName: e.target.value })} />
            </div>
            <div className="field">
              <label>Last name</label>
              <input type="text" value={profile.lastName} onChange={e => setProfile({ ...profile, lastName: e.target.value })} />
            </div>
          </div>
          <div className="field">
            <label>Email</label>
            <input type="email" value={profile.email} onChange={e => setProfile({ ...profile, email: e.target.value })} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button type="submit" className="btn btn-primary" style={{ padding: '9px 22px', fontSize: 13 }} disabled={profileState === 'saving'}>
              Save changes
            </button>
            <SaveFeedback state={profileState} />
          </div>
        </form>
      </Section>

      {/* Password */}
      <Section title="Password" desc="Use a strong password with 8+ characters.">
        <form onSubmit={handlePassSave} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="field">
            <label>Current password</label>
            <input type="password" placeholder="••••••••" value={pass.current} onChange={e => setPass({ ...pass, current: e.target.value })} />
          </div>
          <div className="field-row">
            <div className="field">
              <label>New password</label>
              <input type="password" placeholder="8+ characters" value={pass.next} onChange={e => setPass({ ...pass, next: e.target.value })} />
            </div>
            <div className="field">
              <label>Confirm</label>
              <input type="password" placeholder="Repeat" value={pass.confirm} onChange={e => setPass({ ...pass, confirm: e.target.value })} />
            </div>
          </div>
          {pass.next && pass.confirm && pass.next !== pass.confirm && (
            <div style={{ fontSize: 12, color: '#ff6b6b' }}>Passwords don't match.</div>
          )}
          {passError && <div style={{ fontSize: 12, color: '#ff6b6b' }}>{passError}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button type="submit" className="btn btn-primary" style={{ padding: '9px 22px', fontSize: 13 }}
              disabled={passState === 'saving' || !pass.current || !pass.next || pass.next !== pass.confirm}>
              Update password
            </button>
            <SaveFeedback state={passState} />
          </div>
        </form>
      </Section>

      {/* Business info */}
      {userType === 'business' && (
        <Section title="Business" desc="Workspace details.">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="field">
              <label>Business name</label>
              <input type="text" defaultValue={user?.businessName || ''} placeholder="Acme Technologies" />
            </div>
            <div className="field-row">
              <div className="field">
                <label>Region</label>
                <select defaultValue={user?.region || 'lagos'}>
                  <option value="lagos">Lagos · LAG-01</option>
                  <option value="nairobi">Nairobi · NBO-02</option>
                  <option value="johannesburg">Joburg · JNB-01</option>
                </select>
              </div>
              <div className="field">
                <label>Primary use case</label>
                <select defaultValue={user?.usecase || 'safety'}>
                  <option value="safety">Safety / Civic</option>
                  <option value="fintech">Fintech</option>
                  <option value="energy">Energy</option>
                  <option value="agriculture">Agriculture</option>
                  <option value="health">Health</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <button className="btn btn-primary" style={{ alignSelf: 'flex-start', padding: '9px 22px', fontSize: 13 }}>
              Save business info
            </button>
          </div>
        </Section>
      )}

      {/* Preferences */}
      <Section title="Preferences" desc="Default region and display.">
        <div className="field-row">
          <div className="field">
            <label>Default region</label>
            <select defaultValue="lagos">
              <option value="lagos">Lagos · LAG-01</option>
              <option value="nairobi">Nairobi · NBO-02</option>
              <option value="johannesburg">Joburg · JNB-01</option>
            </select>
          </div>
          <div className="field">
            <label>Timezone</label>
            <select defaultValue="WAT">
              <option value="WAT">West Africa Time (UTC+1)</option>
              <option value="EAT">East Africa Time (UTC+3)</option>
              <option value="SAST">South Africa (UTC+2)</option>
              <option value="UTC">UTC</option>
            </select>
          </div>
        </div>
      </Section>

      {/* Danger zone */}
      <div className="dash-card" style={{ border: '1px solid rgba(255,80,80,0.18)', marginBottom: 20 }}>
        <div className="dash-card-head" style={{ borderBottomColor: 'rgba(255,80,80,0.14)' }}>
          <div>
            <div className="dash-card-title" style={{ color: '#ff6b6b' }}>Danger zone</div>
            <div className="dash-card-desc">Irreversible actions. Proceed carefully.</div>
          </div>
        </div>
        <div className="dash-card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 3 }}>Delete account</div>
            <div style={{ fontSize: 13, color: 'var(--fg-dim)' }}>Permanently delete all keys, data, and history.</div>
          </div>
          {deleteConfirm !== 'confirm' ? (
            <button className="btn btn-ghost" style={{ borderColor: 'rgba(255,80,80,0.3)', color: '#ff6b6b', padding: '8px 16px', fontSize: 12 }}
              onClick={() => setDeleteConfirm('confirm')}>
              Delete account
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" style={{ fontSize: 12, padding: '8px 14px' }} onClick={() => setDeleteConfirm('')}>Cancel</button>
              <button className="btn" style={{ background: '#ff6b6b', color: '#fff', border: 'none', fontSize: 12, padding: '8px 14px' }}>Confirm delete</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
